import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface Campaign {
  id: number;
  name: string;
  templateText: string;
  createdAt: string;
}

const CampaignsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', templateText: '' });

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

  useEffect(() => {
    fetchCampaigns();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API_BASE}/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setCampaigns([]); // Set empty array on error
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE}/campaigns`, formData);
      setFormData({ name: '', templateText: '' });
      setShowForm(false);
      fetchCampaigns();
    } catch (error) {
      console.error('Error creating campaign:', error);
    }
  };

  const deleteCampaign = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    try {
      await axios.delete(`${API_BASE}/campaigns/${id}`);
      fetchCampaigns();
    } catch (error) {
      console.error('Error deleting campaign:', error);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1 className="page-title">Campaigns</h1>
        <button className="btn" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : 'New Campaign'}
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <h3>Create New Campaign</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Campaign Name</label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="templateText">Message Template</label>
              <textarea
                id="templateText"
                value={formData.templateText}
                onChange={(e) => setFormData({ ...formData, templateText: e.target.value })}
                placeholder="Hello &lt;&lt;Name&gt;&gt;, your order #&lt;&lt;Order ID&gt;&gt; has been shipped!"
                required
              />
              <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                Use &lt;&lt;Field Name&gt;&gt; format for dynamic fields from your CSV
              </small>
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button type="submit" className="btn">Create Campaign</button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div>
        {campaigns.length === 0 ? (
          <div className="card">
            <p>No campaigns yet. Create your first campaign to get started!</p>
          </div>
        ) : (
          campaigns.map((campaign) => (
            <div key={campaign.id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 1rem 0', color: '#2c3e50' }}>{campaign.name}</h3>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Template:</strong>
                    <div style={{ 
                      background: '#f8f9fa', 
                      padding: '1rem', 
                      borderRadius: '4px', 
                      marginTop: '0.5rem',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {campaign.templateText}
                    </div>
                  </div>
                  <small style={{ color: '#666' }}>
                    Created: {new Date(campaign.createdAt).toLocaleDateString()}
                  </small>
                </div>
                <button 
                  className="btn btn-danger" 
                  onClick={() => deleteCampaign(campaign.id)}
                  style={{ marginLeft: '1rem' }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CampaignsPage;
